// Showing a song's captions (its timed lines) in the player's caption bar.
// The player has one <video> that is used for every song. Each song's lines are also put on it as a real caption <track>
// (kind "captions"), but only as a signal: a phone or tablet's own captions setting (Safari on iPhone, iPad and Mac)
// knows how to switch a real track on. When the device does that, the track is put back to "hidden" at once (so the
// browser never draws words over the picture) and the player's own bar shows the words instead.
// Nothing else here knows about React.

const KEY = "dtm_captions";
const STYLE_KEY = "dtm_cc_style";
const attached = new WeakMap();   // video -> what is on it now: { el, url, onChange, remove }

// The CC button's choice on this device: true (on), false (off), or null when the person has never pressed it. With null
// the device decides, so a phone whose captions setting is on shows them without the button being pressed.
export function readCaptionsPref() {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === "1" ? true : v === "0" ? false : null;
  } catch (e) { return null; }
}
export function writeCaptionsPref(on) {
  try { window.localStorage.setItem(KEY, on ? "1" : "0"); } catch (e) { /* private window: the choice just is not kept */ }
}

// ---- How the caption bar looks (kept per device, like the CC button's choice) ----
// color: "song" (the lights set for the song), "match" (whatever the Lights panel is showing) or "mine" (the person's own
//        color, kept in `hue` as a wheel angle 0-359, 360 white, 361 grey, 362 black, or a typed "#rrggbb")
// glow: 20-100 (how strong the words' light is); size: the bar's thickness in pixels, or null for the usual size
// pos: "bottom" (under the picture) or "top" (above it); words: "steel" (cut out of the plate) or "plain"
// fx: true (the song's light effect plays inside the steel bar) or false
export const BAR_MIN = 14;
export const BAR_MAX = 80;
export const DEFAULT_STYLE = { color: "song", hue: 187, glow: 100, size: null, pos: "bottom", words: "steel", fx: true };
const HEX6 = /^#[0-9a-f]{6}$/i;

const whole = (v, lo, hi, dflt) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return dflt;
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : dflt;
};

export function normStyle(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  let hue = DEFAULT_STYLE.hue;
  if (typeof r.hue === "string" && HEX6.test(r.hue.trim())) hue = r.hue.trim().toLowerCase();
  else if (typeof r.hue === "number" && Number.isFinite(r.hue)) {
    const n = Math.round(r.hue);
    hue = n >= 360 && n <= 362 ? n : ((n % 360) + 360) % 360;
  }
  return {
    color: r.color === "match" || r.color === "mine" ? r.color : "song",
    hue,
    glow: whole(r.glow, 20, 100, DEFAULT_STYLE.glow),
    size: whole(r.size, BAR_MIN, BAR_MAX, null),
    pos: r.pos === "top" ? "top" : "bottom",
    words: r.words === "plain" ? "plain" : "steel",
    fx: r.fx !== false,
  };
}

export function readCaptionStyle() {
  try {
    const raw = window.localStorage.getItem(STYLE_KEY);
    return normStyle(raw ? JSON.parse(raw) : null);
  } catch (e) { return normStyle(null); }
}
export function writeCaptionStyle(style) {
  try { window.localStorage.setItem(STYLE_KEY, JSON.stringify(normStyle(style))); } catch (e) { /* private window: not kept */ }
}

// The bar's usual thickness: thin on a computer, thicker on a phone (a long line wraps to a second line there).
export const defaultBarSize = (width) => (width <= 560 ? 39 : 26);

// The line to show at time `t` (seconds), from lines sorted by start time: [{s, e, t}]. null when no line is on.
export function activeCue(cues, t) {
  if (!Array.isArray(cues) || !cues.length || !(t >= 0)) return null;
  let lo = 0;
  let hi = cues.length - 1;
  let at = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].s <= t) { at = mid; lo = mid + 1; } else hi = mid - 1;
  }
  for (let i = at; i >= 0 && i > at - 6; i -= 1) if (t < cues[i].e) return cues[i];
  return null;
}

// A line as one line of words (a line break inside a caption becomes a space).
export const lineText = (cue) => (cue ? String(cue.t == null ? "" : cue.t).replace(/\s+/g, " ").trim() : "");

// The words go in as text, so a "<" or "&" in a lyric is shown as it is and never read as a caption tag.
export function cueText(text) {
  return String(text == null ? "" : text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function stamp(seconds) {
  const ms = Math.max(0, Math.round(Number(seconds) * 1000) || 0);
  const p = (n, w = 2) => String(n).padStart(w, "0");
  return `${p(Math.floor(ms / 3600000))}:${p(Math.floor(ms / 60000) % 60)}:${p(Math.floor(ms / 1000) % 60)}.${p(ms % 1000, 3)}`;
}

// The lines as a WebVTT text (the caption format a <track> reads). `cues` is [{s: start seconds, e: end seconds, t: words}].
export function toVtt(cues) {
  const out = ["WEBVTT", ""];
  let n = 0;
  for (const c of Array.isArray(cues) ? cues : []) {
    if (!(c && c.e > c.s)) continue;
    n += 1;
    out.push(String(n), `${stamp(c.s)} --> ${stamp(c.e)}`, cueText(c.t), "");
  }
  return out.join("\n");
}

// Takes this song's lines off the video (and the one before it, if it is still there).
export function detachCaptions(video) {
  const was = video && attached.get(video);
  if (!was) return;
  attached.delete(video);
  was.remove();
}

// Puts a song's lines on the video as a caption track, so a device's own captions setting has something to switch on.
// `onDeviceOn()` is called when the device switches that track on (at the start or later); the track is set back to
// "hidden" right away, so the browser draws nothing over the picture. Returns true when the track is on the video.
export function attachCaptions(video, cues, onDeviceOn) {
  detachCaptions(video);
  if (!video || !video.textTracks || typeof document === "undefined") return false;
  if (!Array.isArray(cues) || !cues.some((c) => c && c.e > c.s)) return false;

  const url = URL.createObjectURL(new Blob([toVtt(cues)], { type: "text/vtt" }));
  const el = document.createElement("track");
  el.kind = "captions";
  el.label = "Lyrics";
  el.srclang = "en";
  el.src = url;
  video.appendChild(el);
  const track = el.track;
  const onChange = () => {
    if (!track || track.mode !== "showing") return;
    try { track.mode = "hidden"; } catch (e) { /* a browser that will not hide it: nothing more to do */ }
    if (onDeviceOn) onDeviceOn();
  };
  video.textTracks.addEventListener("change", onChange);
  const remove = () => {
    try { video.textTracks.removeEventListener("change", onChange); } catch (e) { /* gone with the video */ }
    try { if (el.parentNode) el.parentNode.removeChild(el); } catch (e) { /* already gone */ }
    try { URL.revokeObjectURL(url); } catch (e) { /* already gone */ }
  };
  attached.set(video, { el, url, onChange, remove });
  onChange();
  return true;
}
